import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { getAppRouter } from "@/lib/router"
import { rewritePath } from "@/lib/routes"

// How long a soft navigation takes, split into the props fetch (and, from its
// Server-Timing header, the server's phases) and the render that follows, as
// a sampled RouteChange event. `from` and `to` are page patterns
// ("/trip/[[...slug]]"), so the event groups by route, not by entity.

const SAMPLE_RATE = 0.1

// Enough to cover the data fetches of the navigations in flight.
const MAX_DATA_ENTRIES = 20

type PendingNavigation = {
  start: number
  from: string
  url: string
  // A hidden tab defers the next frame until it is shown, so its total would
  // include the time the tab spent in the background.
  hidden: boolean
}

// The performance timeline stops recording resources once its buffer (250 by
// default) is full, which a photo-heavy page reaches in a navigation or two.
// An observer still sees every entry, so the data fetches are kept here.
let dataEntries: PerformanceResourceTiming[] = []
let observer: PerformanceObserver | undefined

function observeDataFetches() {
  if (typeof PerformanceObserver === "undefined") return
  observer = new PerformanceObserver((list) => collect(list.getEntries()))
  observer.observe({ type: "resource", buffered: true })
}

function collect(entries: PerformanceEntryList) {
  for (const entry of entries) {
    if (
      entry instanceof PerformanceResourceTiming &&
      entry.name.includes("/_next/data/")
    ) {
      dataEntries.push(entry)
    }
  }
  dataEntries = dataEntries.slice(-MAX_DATA_ENTRIES)
}

function dataEntryFor(
  url: string,
  start: number
): PerformanceResourceTiming | undefined {
  // Observer callbacks are queued; take what has not been delivered yet.
  if (observer) collect(observer.takeRecords())
  // The router fetches data for the rewritten page path: /hotel/... loads
  // /_next/data/<build>/place/....json.
  const path = rewritePath(url.split(/[?#]/)[0])
  const dataPath = path === "/" ? "/index.json" : `${path}.json`
  return dataEntries
    .filter(
      (entry) =>
        entry.startTime >= start &&
        new URL(entry.name).pathname.endsWith(dataPath)
    )
    .at(-1)
}

function serverPhases(entry: PerformanceResourceTiming | undefined) {
  const phases: Record<string, number> = {}
  for (const timing of entry?.serverTiming ?? []) {
    phases[`server_${timing.name}_ms`] = Math.round(timing.duration)
  }
  return phases
}

// The next frame after the route commits is when the new page is on screen.
function afterNextPaint(callback: () => void) {
  requestAnimationFrame(() => setTimeout(callback, 0))
}

let started = false

// Once per document, on the router singleton: a navigation's start and
// completion must reach the same listener even if React re-renders between.
export function startRouteChangeTiming() {
  if (started || typeof performance === "undefined") return
  started = true
  if (Math.random() >= SAMPLE_RATE) return

  observeDataFetches()

  const router = getAppRouter()
  let pending: PendingNavigation | undefined
  const measuring = new Set<PendingNavigation>()

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return
    if (pending) pending.hidden = true
    for (const navigation of measuring) navigation.hidden = true
  })

  const onStart = (url: string, { shallow }: { shallow: boolean }) => {
    pending = shallow
      ? undefined
      : {
          start: performance.now(),
          from: router.pathname,
          url,
          hidden: document.visibilityState === "hidden",
        }
  }

  const onComplete = (url: string) => {
    const navigation = pending
    pending = undefined
    if (!navigation || navigation.url !== url) return
    const to = router.pathname
    measuring.add(navigation)
    afterNextPaint(() => {
      measuring.delete(navigation)
      if (navigation.hidden) return
      const end = performance.now()
      const data = dataEntryFor(url, navigation.start)
      const dataEnd = data ? data.responseEnd : navigation.start
      sendAnalyticsEvent({
        name: "RouteChange",
        properties: {
          from: navigation.from,
          to,
          total_ms: Math.round(end - navigation.start),
          data_ms: data ? Math.round(data.responseEnd - data.startTime) : 0,
          render_ms: Math.round(end - dataEnd),
          ...serverPhases(data),
        },
      })
    })
  }

  const onError = () => {
    pending = undefined
  }

  router.events.on("routeChangeStart", onStart)
  router.events.on("routeChangeComplete", onComplete)
  router.events.on("routeChangeError", onError)
}
